// SupervisorPinModal.tsx

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  styled,
  keyframes
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { posService } from "../../../../services/pos.service";

// 🔐 Constants
const MAX_PIN_LENGTH = 5;

// 🎨 Keyframe Animation for Shake
const shake = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-15px); }
  40% { transform: translateX(15px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

//error animation
const fadeInOut = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 🎨 Styled Components
const PinButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 12,
  fontSize: 24,
  fontWeight: 600,
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    transform: "scale(1.05)",
  },
}));

const PinDot = styled(Box)<{ filled: boolean; isShaking: boolean }>(({ theme, filled, isShaking }) => ({
  width: 16,
  height: 16,
  borderRadius: "50%",
  border: `2px solid ${isShaking ? theme.palette.error.main : theme.palette.primary.main}`,
  backgroundColor: isShaking && filled ? theme.palette.error.main : filled ? theme.palette.primary.main : "transparent",
  margin: "0 6px",
  transition: "all 0.2s ease",
  animation: isShaking ? `${shake} 0.5s ease-out` : "none",
}));

const KeyboardContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: "relative",
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(4.5),
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: "blur(8px)",
  borderRadius: 2,
  boxShadow: theme.shadows[2],
  marginTop: theme.spacing(3),
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  minHeight: 220,
}));

interface SupervisorPinModalProps {
  open: boolean;
  action: string;
  reasonLabel?: string;
  onClose: () => void;
  onSuccess: (reason: string) => void;
}

export const SupervisorPinModal: React.FC<SupervisorPinModalProps> = ({
  open,
  reasonLabel = "Reason",
  onClose,
  onSuccess,
}) => {
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePinPress = (digit: string) => {
    if (pin.length < MAX_PIN_LENGTH) {
      setPin((prev) => prev + digit);
    }
  };

  const handleClear = () => {
    setPin("");
    setReason("");
    setError(false);
    setIsShaking(false);
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  const handleSubmit = async () => {
    if (pin.length !== MAX_PIN_LENGTH) return;

    setLoading(true);
    try {
      // ✅ Validate PIN with backend
      const result = await posService.validateSupervisorPin({ pin });

      if (result.data.isValid) {
        onSuccess(reason);
        setPin("");
        setReason("");
        setError(false);
        setIsShaking(false);
        onClose();
      } else {
        // ❌ Invalid PIN
        setError(true);
        setIsShaking(true);
        setTimeout(() => {
          setPin(""); // Clear pin after animation
          setError(false);
          setIsShaking(false);
        }, 900);
      }
    } catch (err) {
      console.error("PIN validation failed:", err);
      setError(true);
      setIsShaking(true);
      setTimeout(() => {
        setPin("");
        setError(false);
        setIsShaking(false);
      }, 900);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
        Supervisor Authorization
      </DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1" gutterBottom align="center">
          Enter {MAX_PIN_LENGTH}-digit PIN
        </Typography>

        {/* PIN Display */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            my: 2,
          }}
        >
          {[...Array(MAX_PIN_LENGTH)].map((_, i) => (
            <PinDot
              key={i}
              filled={i < pin.length}
              isShaking={isShaking}
            />
          ))}
        </Box>

        {/* Error Message */}
        <Box
          sx={{
            minHeight: 24,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 1,
            mt: 1,
            transition: "opacity 0.3s ease",
            opacity: error ? 1 : 0,
            pointerEvents: "none",
          }}
        >
          {error ? (
            <Typography
              color="error"
              align="center"
              variant="body2"
              sx={{
                animation: `${fadeInOut} 0.3s forwards`,
              }}
            >
              Wrong PIN
            </Typography>
          ) : (
            <Box component="span" sx={{ visibility: "hidden" }}>
              Placeholder
            </Box>
          )}
        </Box>

        {/* Reason Field */}
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" align="center">
            {reasonLabel}
          </Typography>
          <TextField
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional"
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Numeric Keypad */}
        <KeyboardContainer>
          <Grid container justifyContent="center" alignItems="center" spacing={1}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <Grid item xs={4} key={num}>
                <PinButton onClick={() => handlePinPress(num.toString())}>
                  {num}
                </PinButton>
              </Grid>
            ))}

            <Grid item xs={4}>
              <PinButton onClick={handleClear} color="error">
                Clear
              </PinButton>
            </Grid>

            <Grid item xs={4}>
              <PinButton onClick={() => handlePinPress("0")}>0</PinButton>
            </Grid>

            <Grid item xs={4}>
              <PinButton onClick={handleBackspace}>
                <span style={{ transform: "translateY(2px)", display: "inline-block" }}>
                  ⌫
                </span>
              </PinButton>
            </Grid>
          </Grid>
        </KeyboardContainer>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={pin.length !== MAX_PIN_LENGTH || loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Validating..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};